import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  PsoFormulaParametersSettings,
  PsoFormulaParametersSettingsRequestBody,
  PsoFormulaParametersSettingsSlim,
} from './types';

export const psoFormulaParametersSettingsApi = createApi({
  reducerPath: 'psoFormulaParametersSettingsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/pso-formula-parameters`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: [
    'PsoFormulaParametersSettings',
    'PsoFormulaParametersSettingsList',
  ],
  endpoints: builder => ({
    getPsoFormulaParametersSettingsI: builder.infiniteQuery<
      PsoFormulaParametersSettingsSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => [
        'PsoFormulaParametersSettings',
        'PsoFormulaParametersSettingsList',
      ],
    }),
    getPsoFormulaParametersSettingsById: builder.query<
      PsoFormulaParametersSettings,
      string
    >({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [
        { type: 'PsoFormulaParametersSettings', id },
      ],
    }),
    createPsoFormulaParametersSettings: builder.mutation<
      string,
      PsoFormulaParametersSettingsRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['PsoFormulaParametersSettings'],
    }),
    updatePsoFormulaParametersSettings: builder.mutation<
      void,
      PsoFormulaParametersSettingsRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'PsoFormulaParametersSettings',
        { type: 'PsoFormulaParametersSettings', id },
      ],
    }),
  }),
});

export const {
  useGetPsoFormulaParametersSettingsByIdQuery,
  useCreatePsoFormulaParametersSettingsMutation,
  useUpdatePsoFormulaParametersSettingsMutation,
} = psoFormulaParametersSettingsApi;

export const useGetPsoFormulaParametersInfiniteQuery =
  normalizeInfiniteQueryData<PsoFormulaParametersSettingsSlim>(
    psoFormulaParametersSettingsApi.useGetPsoFormulaParametersSettingsIInfiniteQuery,
  );
